/**
 * Reading the two shapes an email-confirmation link can arrive in. Issue #626.
 *
 * ── Why there are two ────────────────────────────────────────────────────────
 *
 * The DEFAULT Supabase template sends her to Supabase's own domain:
 *
 *   https://<ref>.supabase.co/auth/v1/verify?token=…&type=signup
 *     &redirect_to=https://app.kinyoudoitall.com/auth/callback
 *
 * Supabase verifies there and **302-redirects** to
 * `/auth/callback#access_token=…`. iOS fires a Universal Link only when the
 * user taps a link already on an associated domain — never on a server-side
 * redirect into one. So the tap target is `supabase.co`, iOS hands it to
 * Safari, and Safari follows the 302 itself. The app is never consulted.
 *
 * ⚠️ That is why #228 could not have worked. It fixed the LANDING path and
 * left the TAP TARGET on supabase.co, so no number of app rebuilds would have
 * changed the outcome. The AASA file was never the problem.
 *
 * The fix is a template that points at OUR domain and carries the token:
 *
 *   https://app.kinyoudoitall.com/auth/callback?token_hash=…&type=signup
 *
 * Now the tap target IS an associated domain, iOS can hand it to the app, and
 * `verifyOtp` establishes the session in whatever context opened the link.
 *
 * ── ⚠️ BOTH shapes must keep working, and that is not optional ───────────────
 *
 * Confirmation emails already sitting in inboxes carry the OLD format. The
 * template change is a Dashboard setting Kasandra flips by hand, so there is no
 * moment where every outstanding link switches over. If this code stopped
 * honouring the hash form, every unconfirmed signup from before the flip would
 * break — and those are people who have not yet gotten into the app at all.
 *
 * ── Deploy order ────────────────────────────────────────────────────────────
 *
 * Code first, template second. The reverse — template first — hands
 * `token_hash` links to a deployed app that only knows how to read a hash, and
 * confirmation breaks for everyone who signs up in between.
 */

/**
 * The OTP types Supabase's email links can carry.
 *
 * Deliberately a closed list rather than passing whatever arrives through to
 * `verifyOtp`: `type` comes from a URL, and the value is handed straight to an
 * auth call. An unrecognised one is treated as no token at all, which falls
 * back to the hash path rather than erroring.
 */
export const EMAIL_OTP_TYPES = [
  "signup",
  "recovery",
  "invite",
  "magiclink",
  "email",
  "email_change",
] as const;

export type EmailOtpType = (typeof EMAIL_OTP_TYPES)[number];

export interface EmailLinkToken {
  tokenHash: string;
  type: EmailOtpType;
}

/**
 * The `token_hash` + `type` pair from a query string, or null when the link is
 * the older hash-fragment kind (or malformed).
 *
 * Null is the normal, expected answer for every link sent before the template
 * change — the caller then does nothing and lets supabase-js's
 * `detectSessionInUrl` parse the fragment exactly as it always has.
 */
export function readEmailLinkToken(search: string): EmailLinkToken | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search || "");
  } catch {
    return null;
  }

  const tokenHash = params.get("token_hash");
  const type = params.get("type");

  if (!tokenHash || !type) return null;
  if (!(EMAIL_OTP_TYPES as readonly string[]).includes(type)) return null;

  return { tokenHash, type: type as EmailOtpType };
}

/**
 * Does this URL carry a legacy hash-fragment session?
 *
 * Only used to tell "waiting for supabase-js to parse the fragment" apart from
 * "this link carried nothing at all" — the second deserves the expired message
 * immediately rather than after the six-second grace window.
 */
export function hasHashSession(hash: string): boolean {
  if (!hash) return false;
  const h = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(h).has("access_token");
}
